import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import MartyLink from 'components/common/MartyLink';
import { pluralize } from 'helpers';
import { isOrderCancellable, isOrderReturnable } from 'helpers/MyAccountUtils';
import css from 'styles/components/account/orderSummary.scss';

const OrderSummary = ({ order, selectedItems, onCancelItems, onReturnItems, showCancelItemsMobile, showReturnItemsMobile, showZenReturns }, { marketplace, testId }) => {
  const { lineItems, pricing } = order;
  const {
    account: { multiBoxReturns, martyReturns, hideAllReturns },
    returns: { details, moreInfoUrl }
  } = marketplace;
  const shouldBeNonReturnable = showZenReturns ? !showZenReturns : hideAllReturns; // if we are shwoing zen returns we want the opposite, so the returnable state is viewable

  return (
    <div className={css.orderSummary}>
      <div className={css.orderActions}>
        {isOrderCancellable(lineItems) && (
          <button
            type="button"
            onClick={onCancelItems}
            disabled={!selectedItems.length}
            data-test-id={testId('cancelItemsButton')}>
            Cancel Checked Items
          </button>
        )}
        {isOrderReturnable(lineItems, shouldBeNonReturnable) && (
          <>
            <button
              type="button"
              onClick={onReturnItems}
              disabled={!selectedItems.length}
              data-test-id={testId('returnItemsButton')}>
              Return Checked Items
            </button>
            { martyReturns && multiBoxReturns &&
                <MartyLink
                  className={css.addAdditional}
                  to="/returns"
                  data-test-id={testId('returnAllItemsLink')}>
                  Add Additional Items To Your Return
                </MartyLink>
            }
          </>
        )}
        <div className={css.shippingNote}>
          <div className={css.shippingNoteDetails}>{details}</div>
          <MartyLink to={moreInfoUrl}>Learn More About Return Shipping</MartyLink>
        </div>
      </div>
      <div className={cn(css.priceSummary, { [css.hasSelectionColumn]: isOrderReturnable(lineItems, shouldBeNonReturnable) || isOrderCancellable(lineItems) })}>
        <h2 className={css.summaryHeading}>Order Summary</h2>
        <dl>
          {/* 1) Subtotal */}
          <div>
            <dt data-test-id={testId('subtotalString')}>Subtotal ({lineItems.length} {pluralize('item', lineItems.length)}):</dt>
            <dd data-test-id={testId('subtotal')}>{pricing.subtotal}</dd>
          </div>
          {/* 2) Discount(s) */}
          <div>
            {pricing.pretax.map(({ description }, index) => (
              <dt key={index} data-test-id={testId('pretaxDesc')}>{description}:</dt>
            ))}
            {pricing.pretax.map(({ amount }, index) => (
              <dd key={index} data-test-id={testId('pretaxDiscount')}>{amount}</dd>
            ))}
          </div>
          {/* 3) Shipping */}
          <div>
            <dt>Shipping:</dt>
            <dd data-test-id={testId('shippingCost')}>{pricing.shipping === '$0.00' ? 'FREE' : pricing.shipping}</dd>
          </div>
          {/* 4) Shipping Discounts */}
          <div>
            {pricing.shippingDiscounts.map(({ description }, index) => (
              <dt key={index} data-test-id={testId('shippingDiscountDesc')}>{description}:</dt>
            ))}
            {pricing.shippingDiscounts.map(({ amount }, index) => (
              <dd key={index} data-test-id={testId('shippingDiscount')}>{amount}</dd>
            ))}
          </div>
          {/* 5) Tax */}
          <div>
            <dt>Collected Tax:</dt>
            <dd data-test-id={testId('tax')}>{pricing.tax}</dd>
          </div>
          {/* 6) Voucher */}
          <div>
            {pricing.posttax.map(({ description }, index) => (
              <dt key={index} data-test-id={testId('voucherDesc')}>{description}:</dt>
            ))}
            {pricing.posttax.map(({ amount }, index) => (
              <dd key={index} data-test-id={testId('voucherAmount')}>{amount}</dd>
            ))}
          </div>
          {/* 7) Order Total */}
          <div>
            <dt className={css.total}>Order Total:</dt>
            <dd className={css.total} data-test-id={testId('totalCost')}>{pricing.total}</dd>
          </div>
          {/* 8) Reversals (Credit) */}
          <div>
            {pricing.reversals.map(({ description }, index) => (
              <dt key={index} data-test-id={testId('reversalDesc')}>{description}:</dt>
            ))}
            {pricing.reversals.map(({ amount }, index) => (
              <dd key={index} data-test-id={testId('reversalAmount')}>{amount}</dd>
            ))}
          </div>
          {/* 9) Total Charged */}
          <div>
            <dt className={css.totalCharged}>Total Charged:</dt>
            <dd className={css.totalCharged} data-test-id={testId('totalCharged')}>{pricing.totalCharged}</dd>
          </div>
        </dl>
      </div>
      {/* mobile view */}
      {isOrderCancellable(lineItems) &&
        <button
          type="button"
          data-test-id={testId('showCancelItemsMobile')}
          onClick={showCancelItemsMobile}
          className={css.cancelBtn}>
          Cancel Items
        </button>}
      {isOrderReturnable(lineItems, shouldBeNonReturnable) &&
        <button
          type="button"
          data-test-id={testId('showReturnItemsMobile')}
          onClick={showReturnItemsMobile}
          className={css.returnBtn}>
          Start A Return
        </button>}
    </div>
  );
};

OrderSummary.contextTypes = {
  testId: PropTypes.func,
  marketplace: PropTypes.object
};

OrderSummary.propTypes = {
  order: PropTypes.object.isRequired,
  selectedItems: PropTypes.array.isRequired,
  onCancelItems: PropTypes.func.isRequired,
  onReturnItems: PropTypes.func.isRequired
};

export default OrderSummary;
